import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@/config/i18n';
import PATH from '@/routes/path';
import { ProgressDataType } from '@/types/progress';
import { CoachProgress, CommentsDialog, WorksDialog } from '@/pages/lesson/progress';
import ProgressFormSearch from '@/pages/lesson/progress/ProgressFormSearch';

/**
 * Progress of the coaches this mentor supervises, for one lesson.
 *
 * Deliberately not reusing LessonProgress: that screen is a TabView whose
 * student tab always requests `type=STUDENT`, which the API rejects with 422
 * for a mentor. Rendering CoachProgress on its own means no such request can
 * be made at all, and the admin/coach progress screen stays untouched.
 */
const MentorLessonProgressPage: React.FC = () => {
  const [t] = useTranslation('');
  const navigate = useNavigate();
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isOpenCommentModal, setIsOpenCommentModal] = useState(false);
  const [isOpenWorkModal, setIsOpenWorkModal] = useState(false);
  const [unitId, setUnitId] = useState<number>(0);
  const [rowData, setRowData] = useState<ProgressDataType | null>(null);

  return (
    <>
      <div className="header-top">
        <h2 className="title flex">
          <span
            className="pi pi-arrow-left text-[20px] text-gray-500 cursor-pointer"
            onClick={() => navigate(PATH.mentor_lesson_management)}
          ></span>
          <span className="ml-3">{t('lesson_progress.page_title')}</span>
        </h2>
      </div>
      <ProgressFormSearch
        placeholderMsg={t('user_list.keyword_coach')}
        onSearch={() => setSearchTrigger((prev) => prev + 1)}
      />
      <CoachProgress
        setIsOpenCommentModal={setIsOpenCommentModal}
        setIsOpenWorkModal={setIsOpenWorkModal}
        setRowData={setRowData}
        setUnitId={setUnitId}
        searchTrigger={searchTrigger}
        emptyMessage={t('lesson_progress.mentor_empty')}
      />

      {isOpenWorkModal && (
        <WorksDialog
          isOpenWorkModal={isOpenWorkModal}
          setIsOpenWorkModal={setIsOpenWorkModal}
          rowData={rowData}
          unitId={unitId}
        />
      )}
      {isOpenCommentModal && (
        <CommentsDialog
          isOpenCommentModal={isOpenCommentModal}
          setIsOpenCommentModal={setIsOpenCommentModal}
          rowData={rowData}
          unitId={unitId}
          // Every row on this screen is a coach, so the comment avatars use
          // the same colours as the admin screen's coach tab.
          isSelectCoachTab
        />
      )}
    </>
  );
};

export default MentorLessonProgressPage;
